import { useMemo } from 'react';

import type { Tab } from '../../../components/Tabs';
import { TabsView } from '../../../components/Tabs';
import type { Company } from '../../../domain/company';
import { TRAFFIC_VISITS_TAB_KEY } from '../../../domain/user';
import { useStoredTabState } from '../../../hooks/useStoredTabState';
import { translate } from '../../../services/i18n';
import { SectionHeader } from '../SectionHeader';
import { HistorycalDataIcon } from '../SectionHeader/HistorycalDataIcon';
import { StyledSectionView } from '../styles';
import { StyledTrafficTabsContainer } from '../TrafficShare/styles';

import { TrafficVisitsContent } from './TrafficVisitsContent';
import { useTrafficVisitsTracking } from './useTrafficVisitsTracking';

export enum TrafficVisitsTab {
  MONTHLY = 'monthly',
  UNIQUE = 'unique',
}

export const TrafficVisits = ({ company }: { company: Company }) => {
  const { trackSelectTab } = useTrafficVisitsTracking(company);
  const { activeTab, onSelectTab } = useStoredTabState<TrafficVisitsTab>({
    storageKey: TRAFFIC_VISITS_TAB_KEY,
    defaultTab: TrafficVisitsTab.MONTHLY,
    tabs: Object.values(TrafficVisitsTab),
  });

  const handleSelectTab = (tabName: TrafficVisitsTab) => {
    onSelectTab(tabName);
    trackSelectTab(tabName);
  };
  const totalVisitsTrend = company.totalVisits?.trend;
  const totalVisitsTrendLength = totalVisitsTrend?.length ?? 0;

  const showHistorycalDataIcon = totalVisitsTrendLength > 0 && totalVisitsTrendLength < 12;

  const tabs: Tab<TrafficVisitsTab>[] = useMemo(
    () => [
      {
        name: TrafficVisitsTab.MONTHLY,
        label: translate('company.traffic_visits.monthly'),
        component: () => <TrafficVisitsContent trend={company.totalVisits?.trend} />,
      },
      {
        name: TrafficVisitsTab.UNIQUE,
        label: translate('company.traffic_visits.unique'),
        component: () => <TrafficVisitsContent trend={company.uniqueVisits?.trend} />,
      },
    ],
    [company]
  );

  return (
    <StyledSectionView data-automation="TrafficOverTime">
      <SectionHeader
        title={translate('company.traffic_visits')}
        websource="all"
        showWorldwideIcon
        HistorycalDataIconComponent={
          showHistorycalDataIcon ? <HistorycalDataIcon numberOfMonths={totalVisitsTrendLength} /> : undefined
        }
      />
      <StyledTrafficTabsContainer data-automation="TrafficTabs">
        <TabsView<TrafficVisitsTab>
          tabsPosition="top"
          variant="compact"
          activeTabName={activeTab}
          onSelect={handleSelectTab}
          tabs={tabs}
        />
      </StyledTrafficTabsContainer>
    </StyledSectionView>
  );
};
